import { push } from '@lagunovsky/redux-react-router'
import { Card, Grid, Icon, Stack, Typography } from '@mui/material'
import AdministrationAction from 'administration/actions/AdministrationAction'
import { PATH_REFERENCIAL_LINK } from 'home/constants/RouteConstants'
import JobAction from 'import/actions/JobAction'
import { lowerCase, orderBy } from 'lodash'
import React, { useEffect, useMemo, useState } from 'react'
import i18n from 'simple-react-i18n'
import { useDispatch } from 'react-redux'
import { getFullDate } from 'utils/DateUtil'
import AvatarIcon from '@mui/material/Avatar'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import CountUp from 'react-countup'
import JobSyncStatus from 'referencial/components/JobSyncStatus'
import useActions from 'utils/customHook/useActions'
import { getDataWithHeaders } from 'utils/ExportDataUtil'
import PropTypes from 'prop-types'

const headers = ['referencialName', 'updateDate', 'referencialVersion', 'updateStatus', 'updateUser', 'recordingNumber']

const ReferencialCard = ({
    referencials,
    referencialSandreCodes,
    sizeCard= 3,
    url= '',
    name= '',
    icon= '',
    id=-1,
    number=0,
    link=PATH_REFERENCIAL_LINK,
}) => {
    const dispatch = useDispatch()

    const [picture, setPicture] = useState()
    const [imageLoaded, setImageLoaded] = useState(false)

    const [jobSyncs, setJobSyncs] = useState({})

    useEffect(() => {
        if (referencials.length) {
            const referencialWithSync = referencials.filter(r => r.id === id && r.syncJobId)
            const timer = setTimeout(() => {
                Promise.all(referencialWithSync.map(r => JobAction.promiseFullJob(r.syncJobId, 1))).then(jobs => {
                    setJobSyncs(referencialWithSync.reduce((acc, referential, idx) => {
                        if (jobs[idx]?.executions?.[0]) {
                            acc[referential.id] = { ...jobs[idx], exec: jobs[idx].executions[0] }
                        }
                        return acc
                    }, {}))
                })
            }, 0)
            return () => clearTimeout(timer)
        }
        return undefined
    }, [referencials])

    useEffect(() => {
        if (!imageLoaded && referencialSandreCodes.length) {
            const login = referencialSandreCodes.find(o => o.field === id)?.updateUser
            if (!login) return
            dispatch(AdministrationAction.fetchProfilePicture(login)).then(res => {
                const extension = res.name.substring(res.name.lastIndexOf('.'), res.name.length)
                if (extension.length > 0) {
                    const namePic = `${login}${extension}`
                    dispatch(AdministrationAction.fetchPictureWithoutCache(namePic)).then(urlPic => {
                        setPicture(urlPic)
                        setImageLoaded(true)
                    })
                } else {
                    setImageLoaded(true)
                }
            })
        }
    }, [referencialSandreCodes, id])

    const getDate = fieldReferencial => {
        const findReferencialUpdateDate = referencialSandreCodes.find(o => o.field === fieldReferencial)
        return (findReferencialUpdateDate && findReferencialUpdateDate.updateDate) && i18n.updatedOn + getFullDate(findReferencialUpdateDate.updateDate)
    }

    const getIconUpdateSandre = updateSandre => {
        const findUpdateSandre = referencialSandreCodes.find(o => o.field === updateSandre)
        return (findUpdateSandre && findUpdateSandre.updateSandre === '1') && 'wifi'
    }

    const getUserName = referentialId => {
        const findUserName = referencialSandreCodes.find(o => o.field === referentialId)
        return (findUserName && findUserName.updateUser) ? findUserName.updateUser : i18n.user.toLowerCase()
    }

    const getReferentialNumber = referentialNumber => {
        const findReferentialNumber = referencialSandreCodes.find(o => o.field === referentialNumber)
        return (findReferentialNumber && findReferentialNumber.referentialNumber) ? `v ${findReferentialNumber.referentialNumber}` : ''
    }

    const referencialData = useMemo(() => orderBy(referencials, 'name', 'asc').map(referencial => ({
        referencialName: referencial.name,
        updateDate: getDate(referencial.id),
        updateStatus: getIconUpdateSandre(referencial.id) === '1' ? 'automatique' : 'manuelle',
        updateUser: getUserName(referencial.id).toString(),
        recordingNumber: parseInt(referencial.number.toLocaleString()),
        referencialVersion: getReferentialNumber(referencial.id),
    })), [referencials, referencialSandreCodes])

    useActions(() => {
        return {
            referencialActions: {
                actions: {
                    export: {
                        data: getDataWithHeaders(referencialData, headers),
                        titleFile: i18n.referencials,
                    },
                },
            },
        }
    }, [referencialData])

    return (
        <Grid key={url} className='clickable' size={sizeCard}
            onClick={() =>
                dispatch(push(link + url))}
            data-cy={`referencial_${name}`}
        >
            <Card>
                <Grid container id={lowerCase(id).replaceAll(' ', '_')} >
                    <Grid container size={9} paddingX={2} paddingY={1} spacing={1}>
                        <Grid container size={3} justifyContent='center'>
                            <Icon sx={{ fontSize: '50px' }}>
                                {icon}
                            </Icon>
                        </Grid>
                        <Grid container size={9} alignItems='center'>
                            <Typography fontSize='18px' fontWeight='bold'>{name}</Typography>
                        </Grid>
                        <Grid size={3}>
                            <Stack alignItems='center'>
                                {
                                    picture ? (
                                        <AvatarIcon
                                            src={picture}
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                bgcolor: 'white',
                                            }}
                                        />
                                    ) : <AccountCircleIcon style={{ height: 40, width: 40 }}/>
                                }
                                <Typography fontSize='10px'>
                                    { getUserName(id) }
                                </Typography>
                            </Stack>
                        </Grid>
                    </Grid>
                    <Grid container size={3} alignItems='center' justifyContent='flex-end' paddingRight={2}>
                        <Typography variant='h4' fontWeight='bold'>
                            <CountUp end={number} duration={2} />
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container sx={{ minHeight: '30px', backgroundColor: '#e4e4e4ff', paddingLeft: 1 }}>
                    <Grid container size={10} alignItems='center'>
                        {jobSyncs[id] && jobSyncs[id]?.job?.updateDate ? (
                            <JobSyncStatus jobSync={jobSyncs[id]} smallMode/>
                        ) : (
                            <Typography
                                variant='caption'
                                sx={{ color: 'black', fontStyle: 'italic' }}
                            >
                                {getDate(id)}
                            </Typography>
                        )}
                    </Grid>
                    <Grid container size={1} display='flex' justifyContent='flex-end' alignItems='center'>
                        <Typography variant='caption' sx={{ color: 'black', fontStyle: 'italic' }}>
                            {getReferentialNumber(id)}
                        </Typography>
                    </Grid>
                    <Grid container size={1} display='flex' justifyContent='center' alignItems='center'>
                        <Icon sx={{ fontSize: 'small' }}>
                            {getIconUpdateSandre(id)}
                        </Icon>
                    </Grid>
                </Grid>
            </Card>
        </Grid>
    )
}


ReferencialCard.propTypes = {
    referencials: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            icon: PropTypes.string,
            url: PropTypes.string,
            name: PropTypes.string,
            number: PropTypes.number,
            syncJobId: PropTypes.number,
            date: PropTypes.string,
            habilitation: PropTypes.shape({
                required: PropTypes.array,
                atLeastOne: PropTypes.array,
                requiredRoles: PropTypes.array,
            }),
        }),
    ),
    referencialSandreCodes: PropTypes.arrayOf(
        PropTypes.shape({
            field: PropTypes.string,
            code: PropTypes.number,
            referentialNumber: PropTypes.string,
            updateSandre: PropTypes.string,
            labelWithCode: PropTypes.string,
        }),
    ),
    sizeCard: PropTypes.number,
    url: PropTypes.string,
    name: PropTypes.string,
    icon: PropTypes.string,
    id: PropTypes.string,
    number: PropTypes.number,
    link: PropTypes.string,
}

export default ReferencialCard